import React, { useCallback, useEffect, useState } from 'react';
import { FaArrowLeft, FaCamera, FaSpinner } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { disputeService, resolveProductMediaUrl } from '../../services/apiService';

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function DetailRow({ label, children }) {
  return (
    <div className="border-b border-slate-700/60 py-3 last:border-0">
      <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500">{label}</p>
      <div className="mt-1 text-sm text-slate-200">{children}</div>
    </div>
  );
}

export default function DisputeClaimDetail({ orderId, onBack, onUpdated }) {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [claim, setClaim] = useState(null);
  const [evidenceNote, setEvidenceNote] = useState('');

  const load = useCallback(async () => {
    if (!orderId) return;
    setLoading(true);
    try {
      const res = await disputeService.getDispute(orderId);
      setClaim(res?.dispute || res);
    } catch (err) {
      toast.error(err.message || 'Could not load claim details');
      setClaim(null);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleEvidenceUpload = async (fileList) => {
    const files = Array.from(fileList || []).slice(0, 3);
    if (!files.length) return;
    const encoded = [];
    for (const file of files) {
      encoded.push(await readFileAsDataUrl(file));
    }
    setSubmitting(true);
    try {
      await disputeService.submitEvidenceResponse(orderId, {
        party: 'buyer',
        photos: encoded,
        description: evidenceNote.trim() || 'Additional evidence submitted per admin request',
      });
      toast.success('Evidence submitted');
      setEvidenceNote('');
      await load();
      onUpdated?.();
    } catch (err) {
      toast.error(err.message || 'Could not submit evidence');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16 text-slate-400">
        <FaSpinner className="animate-spin text-2xl" />
      </div>
    );
  }

  if (!claim) {
    return (
      <div className="rounded-2xl border border-slate-700 bg-slate-800/40 px-6 py-10 text-center">
        <p className="text-slate-400">Claim not found for this order.</p>
        <button
          type="button"
          onClick={onBack}
          className="mt-4 text-sm text-amber-400 hover:text-amber-300"
        >
          Go back
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-2 text-sm text-amber-400 hover:text-amber-300"
      >
        <FaArrowLeft /> Back to claims
      </button>

      <div className="rounded-2xl border border-slate-700/60 bg-slate-800/40 p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="font-mono text-xs text-amber-400">{claim.claimId}</p>
            <h2 className="mt-1 text-lg font-bold text-white">{claim.partName || 'Order claim'}</h2>
            <p className="text-xs text-slate-500">{claim.orderId}</p>
          </div>
          <span className="rounded-full border border-slate-600 bg-slate-900 px-3 py-1 text-xs font-semibold text-slate-200">
            {claim.statusChip || claim.status}
          </span>
        </div>

        <div className="mt-4 divide-y divide-slate-700/40">
          <DetailRow label="Issue">{claim.issueLabel || claim.issueType}</DetailRow>
          <DetailRow label="Description">{claim.description || '—'}</DetailRow>
          <DetailRow label="Submitted">
            {claim.submittedAt
              ? new Date(claim.submittedAt).toLocaleString('en-IN', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })
              : '—'}
          </DetailRow>
          {(claim.resolutionDeadline || claim.resolutionEta) && (
            <DetailRow label="Resolution target">
              {new Date(claim.resolutionDeadline || claim.resolutionEta).toLocaleString('en-IN', {
                day: 'numeric',
                month: 'short',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </DetailRow>
          )}
          {claim.outcomeLabel && (
            <DetailRow label="Outcome">{claim.outcomeLabel}</DetailRow>
          )}
          {claim.suggestedFaultType && (
            <DetailRow label="System review">
              {claim.suggestedFaultType.replace(/_/g, ' ')}
              {claim.arbitrationReason ? ` — ${claim.arbitrationReason}` : ''}
            </DetailRow>
          )}
          {claim.adminNotes ? (
            <DetailRow label="Admin note">{claim.adminNotes}</DetailRow>
          ) : null}
        </div>

        {(claim.photos || []).length > 0 && (
          <div className="mt-4">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
              Photos
            </p>
            <div className="flex flex-wrap gap-2">
              {claim.photos.map((photo, idx) => (
                <a
                  key={idx}
                  href={resolveProductMediaUrl(photo.url || photo)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block h-20 w-20 overflow-hidden rounded-lg border border-slate-600"
                >
                  <img
                    src={resolveProductMediaUrl(photo.url || photo)}
                    alt={`Evidence ${idx + 1}`}
                    className="h-full w-full object-cover"
                  />
                </a>
              ))}
            </div>
          </div>
        )}

        {claim.appeal && (
          <div className="mt-4 rounded-xl border border-indigo-700/50 bg-indigo-950/30 px-4 py-3">
            <p className="text-xs font-semibold text-indigo-200">Appeal submitted</p>
            <p className="mt-1 text-xs text-indigo-100/90">
              Status: {claim.appeal.status?.replace(/_/g, ' ') || 'under review'}
            </p>
            {claim.appeal.grounds && (
              <p className="mt-2 text-xs text-slate-300">{claim.appeal.grounds}</p>
            )}
          </div>
        )}

        {claim.status === 'appealed' && !claim.appeal && (
          <div className="mt-4 rounded-xl border border-indigo-700/50 bg-indigo-950/30 px-4 py-3 text-xs text-indigo-200">
            Appeal under senior ops review.
          </div>
        )}

        {claim.photoGracePending && (
          <div className="mt-4 rounded-xl border border-amber-700/50 bg-amber-950/30 px-4 py-3">
            <p className="text-xs font-semibold text-amber-200">
              Delivery photo required within 24 hours
            </p>
            <label className="mt-3 inline-flex cursor-pointer items-center gap-2 rounded-lg bg-amber-500 px-3 py-2 text-xs font-bold text-slate-900 hover:bg-amber-400">
              <FaCamera />
              Upload delivery photo
              <input
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                disabled={submitting}
                onChange={async (e) => {
                  const files = e.target.files;
                  if (!files?.length) return;
                  const encoded = [];
                  for (const file of Array.from(files).slice(0, 5)) {
                    encoded.push(await readFileAsDataUrl(file));
                  }
                  setSubmitting(true);
                  try {
                    await disputeService.uploadPhotos(orderId, encoded);
                    toast.success('Delivery photo uploaded');
                    await load();
                    onUpdated?.();
                  } catch (err) {
                    toast.error(err.message || 'Upload failed');
                  } finally {
                    setSubmitting(false);
                    e.target.value = '';
                  }
                }}
              />
            </label>
          </div>
        )}

        {(claim.status === 'evidence_requested' || claim.pendingBuyerEvidence) && (
          <div className="mt-4 rounded-xl border border-blue-700/50 bg-blue-950/30 px-4 py-3">
            <p className="text-xs font-semibold text-blue-200">
              Admin requested additional evidence
              {claim.pendingBuyerEvidence?.responseDeadline && (
                <>
                  {' '}
                  — respond by{' '}
                  {new Date(claim.pendingBuyerEvidence.responseDeadline).toLocaleString('en-IN', {
                    day: 'numeric',
                    month: 'short',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </>
              )}
            </p>
            {claim.pendingBuyerEvidence?.description && (
              <p className="mt-2 text-xs text-blue-100/80">{claim.pendingBuyerEvidence.description}</p>
            )}
            <textarea
              value={evidenceNote}
              onChange={(e) => setEvidenceNote(e.target.value)}
              rows={2}
              placeholder="Optional note with your evidence…"
              className="mt-3 w-full rounded-lg border border-slate-600 bg-slate-900 px-3 py-2 text-xs text-slate-100"
            />
            <label className="mt-3 inline-flex cursor-pointer items-center gap-2 rounded-lg bg-blue-500 px-3 py-2 text-xs font-bold text-white hover:bg-blue-400">
              <FaCamera />
              Upload requested evidence
              <input
                type="file"
                accept="image/*"
                capture="environment"
                multiple
                className="hidden"
                disabled={submitting}
                onChange={(e) => {
                  handleEvidenceUpload(e.target.files);
                  e.target.value = '';
                }}
              />
            </label>
          </div>
        )}
      </div>
    </div>
  );
}
